/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import './range.less';

import { WatchableValueInterface } from 'neuroglancer/trackable_value';
import { RefCounted } from 'neuroglancer/util/disposable';
import { removeFromParent } from 'neuroglancer/util/dom';
import { viewer } from 'src/main';
import { AnnotationUserLayer } from '../annotation/user_layer';
import { ImageUserLayer } from '../image_user_layer';

export interface RangeWidgetOptions {
  min?: number;
  max?: number;
  step?: number;
  name?: string
}

export class RangeWidget extends RefCounted {
  element = document.createElement('label');
  inputElement = document.createElement('input');
  numericInputElement = document.createElement('input');
  leftBg = document.createElement('div');
  rightBg = document.createElement('div');
  min: number;
  numberDom = document.createElement('div');
  span = document.createElement('span');
  valueChange = false;
  constructor(
    public value: WatchableValueInterface<number>,
    { min = 0, max = 1, step = 0.01, name = 'opacity' }: RangeWidgetOptions = {}, public changeWheel = false) {
    super();
    this.min = min;
    let { element, inputElement, numericInputElement, leftBg, rightBg, span, numberDom, valueChange } = this;
    element.className = 'range-slider';
    leftBg.className = 'left-bg';
    rightBg.className = 'right-bg';
    const initInputElement = (el: HTMLInputElement) => {
      el.min = '' + this.min;
      el.max = '' + max;
      el.step = '' + step;
      el.valueAsNumber = this.value.value;
      this.registerEventListener(el, 'change', () => this.inputValueChanged(el));
      this.registerEventListener(el, 'input', () => this.inputValueChanged(el));
      this.registerEventListener(el, 'wheel', (event: WheelEvent) => {
        this.adjustViaWheel(el, event);
      });
    };
    const opacityInputElement = (el: HTMLInputElement) => {
      el.min = '0';
      el.max = '100';
      el.step = '1';
      el.valueAsNumber = this.value.value * 100;
      this.registerEventListener(el, 'change', () => {
        if( el.valueAsNumber > Number(el.max) || el.valueAsNumber < Number(el.min) || el.valueAsNumber%1 !== 0) return
        this.inputValueChanged(el, 'opacity');
        valueChange = false
        viewer.resetoreJson(viewer.state.toJSON());
      });
    }
    inputElement.type = 'range';
    initInputElement(inputElement);
    leftBg.style.width = ((this.value.value - this.min) / (max - this.min) * 100) + '%';
    rightBg.style.width = ((1 - (this.value.value - this.min) / (max - this.min)) * 100) + '%';
    numericInputElement.type = 'number';
    numericInputElement.max = max.toString();
    numericInputElement.min = min.toString();
    const maxNumberWidth = Math.max(
      min.toString().length, max.toString().length, Math.min(max, min + step).toString().length,
      Math.max(min, max - step).toString().length);
    numericInputElement.style.width = (maxNumberWidth + 2) + 'ch';
    span.innerHTML = '%';
    element.appendChild(inputElement);
    element.appendChild(numericInputElement);
    const nameArr = ['opacity', 'oFactor', 'spot size', 'sizeFactor','sizefactor', 'ofactor'];   //需要做成百分比的滑动条
    if( nameArr.includes( name.toLocaleLowerCase() ) ) {
      numberDom.classList.add('opacity-containter');
      numberDom.appendChild(numericInputElement);
      numberDom.appendChild(span);
      numericInputElement.style.display = 'block';
      element.appendChild(numberDom);
      numericInputElement.valueAsNumber = this.value.value * 100;
      opacityInputElement(numericInputElement);
    }else{
      initInputElement(numericInputElement);
    }
    element.appendChild(leftBg);
    element.appendChild(rightBg);
    value.changed.add(() => {
      const json = viewer.state.toJSON();
      let layers = json.layers || [];
      layers.forEach((item: any) => { if (item.name === json.selectedLayer?.layer) item.shaderControls[name] = this.value.value })
      this.inputElement.valueAsNumber = this.value.value;
      this.numericInputElement.valueAsNumber = ( nameArr.includes( name.toLocaleLowerCase() ) ) ? Math.floor( this.value.value * 100 ) : this.value.value;
      leftBg.style.width = ((this.value.value - this.min) / (max - this.min) * 100) + '%';
      rightBg.style.width = ((1 - (this.value.value - this.min) / (max - this.min)) * 100) + '%';
      if(name !== 'scale'){
        const json = viewer.state.toJSON();
        let layers = json.layers || [];
        const isHeatmap = viewer.selectedLayer.layer?.heatmap;
        layers.forEach((item: any) => { 
          if (item.name === json.selectedLayer?.layer){
            item.shaderControls[name ? name : 'opacity'] = this.value.value;
            item[`${ name ? name : 'opacity'}`] = this.value.value;
          }
          // 如果是gef文件，集体修改
          if(isHeatmap && item.heatmap){
            item.shaderControls[name ? name : 'opacity'] = this.value.value;
            item[`${ name ? name : 'opacity'}`] = this.value.value;
          }
        });
        viewer.layerManager.managedLayers.forEach((item:any)=>{
          if (item.name === viewer.selectedLayer?.layer?.name){
            item.shaderControls[name ? name : 'opacity'] = this.value.value;
            if(!name && item.layer['opacity'])item.layer['opacity'].value = this.value.value;
            if(item.type === 'segmentation'){
              item.layer['selectedAlpha'] = this.value.value;
              item.shaderControls['selectedAlpha'] = this.value.value;
            }
          }
          // 如果是gef文件，集体修改
          if(isHeatmap && item.layer.heatmap){
            item.shaderControls[name ? name : 'opacity'] = this.value.value;
          }
        });
        if(viewer.selectedLayer.layer?.genetype === 'transform'){
          const obj = viewer.selectedLayer.layer!.layer?.shaderControls;
          if(obj)obj[name] = this.value.value;
          (viewer.selectedLayer.layer!.layer as ImageUserLayer).shaderControlState!.restoreState(obj)
        }
      }
      valueChange = true;
    });
    this.registerEventListener(inputElement, 'mouseup', ()=> {
      if(valueChange) viewer.resetoreJson(viewer.state.toJSON());
      valueChange = false;
      const isHeatmap = viewer.selectedLayer.layer?.heatmap;
      viewer.layerManager.managedLayers.forEach((item:any)=>{
        if(isHeatmap && item.heatmap){
          item.shaderControls[name ? name : 'opacity'] = this.value.value;
          let obj = {};
          obj = JSON.parse( JSON.stringify(item.shaderControls) );
          (item.layer as AnnotationUserLayer).annotationDisplayState.shaderControls.restoreState(obj)
        }
      })
    });
  }

  private inputValueChanged(element: HTMLInputElement, value?: string) {
    // if( element.valueAsNumber > Number(element.max) || element.valueAsNumber < Number(element.min) || element.valueAsNumber%1 !== 0 )return
    this.value.value = value ? element.valueAsNumber / 100 : element.valueAsNumber;
  }

  adjustViaWheel(element: HTMLInputElement, event: WheelEvent) {
    const el = this.inputElement;
    let { deltaY } = event;
    if (deltaY > 0) {
      if(this.changeWheel) {
        this.stepChange('down')
      } else {
        el.stepDown();
        this.inputValueChanged(element);
      }
    } else if (deltaY < 0) {
      if(this.changeWheel) {
        this.stepChange('up')
      } else {
        el.stepUp();
        this.inputValueChanged(element);
      }
    }
  }

  stepChange(type: string) {
    const el = this.inputElement;
    let val = el.valueAsNumber;
    if (type === 'up') {
      if (val >= 1) {
        el.valueAsNumber += 0.1;
      } else if (val >= 0.01) {
        el.valueAsNumber += 0.01;
      } else {
        el.valueAsNumber += 0.001;
      }
    } else if (type === 'down') {
      if (val <= 0.01) {
        el.valueAsNumber -= 0.001;
      } else if (val <= 1) {
        el.valueAsNumber -= 0.01;
      } else {
        el.valueAsNumber -= 0.1;
      }
    }
    this.inputValueChanged(el);
  }

  restoreMin(min: number) {
    this.min = min;
    this.inputElement.min = '' + min;
  }

  disposed() {
    removeFromParent(this.element);
    super.disposed();
  }
}
